import { createReducer, on } from '@ngrx/store';

import { ReportEditionState } from 'app/shared/report';
import { ErrorMessage } from 'shared/error-handling';
import {
    KpiSetDetails,
    ReportDetails,
    ReportEditionDetails,
    ReportEditionListItem,
} from 'shared/report-model';
import { OperationTimeDto, ReportTemplate } from 'shared/report-template/report-template-adapter/model';

import {
    ReportDetailsApiActions,
    ReportDetailsUsersActions,
    ReportEditionDetailsApiActions,
    ReportEditionDetailsUserActions,
} from '../actions';

export interface State {
    report: ReportDetails;
    reportError?: ErrorMessage;
    reportEditions: ReportEditionListItem[];
    reportEdition: ReportEditionDetails;
    reportEditionError?: ErrorMessage;
    latestReportTemplate: ReportTemplate;
    kpiSetDetails: KpiSetDetails;
    filterOperationTime: OperationTimeDto;
    showReportLoader: boolean;
    showReportContentLoader: boolean;
    dialogError?: ErrorMessage;
    isPrinting: boolean;
}

export const initialState: State = {
    report: undefined,
    reportEditions: [],
    reportEdition: undefined,
    latestReportTemplate: undefined,
    kpiSetDetails: undefined,
    filterOperationTime: undefined,
    showReportLoader: false,
    showReportContentLoader: false,
    isPrinting: false,
};

export const reportDetailsReducer = createReducer(
    initialState,
    on(
        ReportDetailsUsersActions.openReportDetailsPage,
        ReportDetailsUsersActions.openKpiSetDetailsPage,
        () => {
            return {
                ...initialState,
                showReportLoader: true,
            };
        },
    ),
    on(ReportDetailsApiActions.getReportSuccess, (state, { report }) => {
        return {
            ...state,
            report,
            reportError: undefined,
        };
    }),
    on(ReportDetailsApiActions.getReportFailure, (state, { errorMessage }) => {
        return {
            ...state,
            report: undefined,
            reportError: errorMessage,
        };
    }),
    on(ReportDetailsApiActions.getReportSuccess, ReportDetailsApiActions.getReportFailure, (state) => {
        return {
            ...state,
            showReportLoader: false,
        };
    }),
    on(
        ReportDetailsUsersActions.activateRecurringReport,
        ReportDetailsUsersActions.publishOneOffReport,
        (state) => {
            return {
                ...state,
                showReportContentLoader: true,
            };
        },
    ),
    on(ReportDetailsUsersActions.editReportDefinition, (state, { id, name, description }) => {
        return {
            ...state,
            report: {
                ...state.report,
                id,
                name,
                description,
            },
        };
    }),
    on(
        ReportDetailsApiActions.activateReportFailure,
        ReportDetailsApiActions.activateReportSuccess,
        (state) => {
            return {
                ...state,
                showReportContentLoader: false,
            };
        },
    ),
    on(ReportEditionDetailsApiActions.putRegenerateEditionSuccess, (state, { periodValidationStatus }) => {
        return {
            ...state,
            reportEdition: {
                ...state.reportEdition,
                state: periodValidationStatus,
            },
        };
    }),
    on(ReportEditionDetailsApiActions.putRegenerateEditionFailure, (state, { periodValidationStatus }) => {
        return {
            ...state,
            reportEdition: {
                ...state.reportEdition,
                state: periodValidationStatus ? periodValidationStatus : ReportEditionState.Failed,
            },
        };
    }),
    on(ReportEditionDetailsUserActions.openReportEdition, (state) => {
        return {
            ...state,
            reportEdition: undefined,
            showReportContentLoader: true,
        };
    }),
    on(ReportEditionDetailsUserActions.openKpiSetDetails, (state, { kpiSetDetails }) => {
        return {
            ...state,
            kpiSetDetails: kpiSetDetails,
        };
    }),
    on(ReportEditionDetailsApiActions.getReportEditionSuccess, (state, { reportEdition }) => {
        return {
            ...state,
            reportEdition,
            reportError: undefined,
        };
    }),
    on(ReportEditionDetailsApiActions.getReportEditionFailure, (state, { errorMessage }) => {
        return {
            ...state,
            reportEdition: undefined,
            reportEditionError: errorMessage,
        };
    }),
    on(
        ReportEditionDetailsApiActions.getReportEditionSuccess,
        ReportEditionDetailsApiActions.getReportEditionFailure,
        (state) => {
            return {
                ...state,
                showReportContentLoader: false,
            };
        },
    ),
    on(ReportDetailsApiActions.getLatestReportTemplateSuccess, (state, { template }) => {
        return {
            ...state,
            latestReportTemplate: template,
        };
    }),
    on(ReportDetailsApiActions.getReportEditionListSuccess, (state, { editions }) => {
        return {
            ...state,
            reportEditions: editions,
        };
    }),
    on(ReportDetailsUsersActions.deleteReport, (state) => {
        return {
            ...state,
            dialogError: undefined,
        };
    }),
    on(ReportDetailsApiActions.deleteReportSuccess, (state) => {
        return {
            ...state,
            dialogError: undefined,
        };
    }),
    on(ReportDetailsApiActions.deleteReportFailure, (state, { errorMessage }) => {
        return {
            ...state,
            dialogError: errorMessage,
        };
    }),
    on(ReportEditionDetailsApiActions.getFilterOperationTimeSuccess, (state, { filterOperationTime }) => {
        return {
            ...state,
            filterOperationTime: filterOperationTime,
        };
    }),
    on(ReportDetailsUsersActions.printReport, (state, { isPrinting }) => {
        return {
            ...state,
            isPrinting: isPrinting,
        };
    }),
);
